import React, { useState, useEffect, useCallback } from 'react';
import { gtplSaathiAPI } from '../services/api';
import GtplSaathiAutoLogin from './GtplSaathiAutoLogin';
import toast from 'react-hot-toast';
import { CheckCircle, XCircle, KeyRound, RefreshCw, SlidersHorizontal, History, ClipboardPaste } from 'lucide-react';
import useDarkReady from '../theme/useDarkReady';
import { Screen, Panel, Notice, SwitchRow, Loading, Item } from '../components/gtpl/GtplKit';

function fmtDateTime(d) {
  return d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
}

function fmtRelative(d) {
  if (!d) return '';
  const mins = Math.round((Date.now() - new Date(d).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

// e.g. 47 -> "47m", 130 -> "2h 10m", 1500 -> "1d 1h"
function fmtDuration(mins) {
  if (mins == null) return '';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  if (hrs < 24) return rem ? `${hrs}h ${rem}m` : `${hrs}h`;
  const days = Math.floor(hrs / 24);
  const remHrs = hrs % 24;
  return remHrs ? `${days}d ${remHrs}h` : `${days}d`;
}

// gtplsaathi.com's login has a CAPTCHA. Either a staff member logs in themselves and pastes the
// session cookie (below), or "Automatic login" reads the captcha and signs in by itself (backend
// gtplSaathiLogin.js). See gtplSaathiService.js / IntegrationSettings.gtplSaathi for how it's used.
export default function GtplSaathiSettingsTab() {
  useDarkReady();
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [cookie, setCookie] = useState('');
  const [saving, setSaving] = useState(false);
  const [togglingNav, setTogglingNav] = useState(false);
  const [togglingDisconnect, setTogglingDisconnect] = useState(false);
  const [togglingLongTerm, setTogglingLongTerm] = useState(false);
  const [togglingAastha, setTogglingAastha] = useState(false);
  const [checking, setChecking] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoadingStatus(true);
    try {
      const { data } = await gtplSaathiAPI.getSessionStatus();
      setStatus(data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load GTPL Saathi status');
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  async function handleSave() {
    if (!cookie.trim()) return toast.error('Paste the Cookie header value first');
    setSaving(true);
    try {
      await gtplSaathiAPI.saveSession(cookie.trim());
      toast.success('Session saved — anything queued in the Retry Queue is now replaying in the background');
      setCookie('');
      loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save session');
    } finally {
      setSaving(false);
    }
  }

  async function handleCheckNow() {
    setChecking(true);
    try {
      const { data } = await gtplSaathiAPI.testSession();
      if (data.data.ok) {
        toast.success('GTPL Saathi session is working');
      } else {
        toast.error('Session expired — paste a fresh cookie below');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Session check failed');
    } finally {
      setChecking(false);
      loadStatus();
    }
  }

  async function handleToggleNav(next) {
    setTogglingNav(true);
    // Optimistic — this is a low-stakes display preference, not a save-then-confirm action.
    setStatus(s => ({ ...s, showInNav: next }));
    try {
      await gtplSaathiAPI.setNavVisibility(next);
    } catch (err) {
      setStatus(s => ({ ...s, showInNav: !next }));
      toast.error(err.response?.data?.message || 'Failed to update');
    } finally {
      setTogglingNav(false);
    }
  }

  async function handleToggleRenewalAastha(next) {
    setTogglingAastha(true);
    setStatus(s => ({ ...s, renewalAastha: next }));
    try {
      await gtplSaathiAPI.setRenewalAastha(next);
      toast.success(next ? 'AASTHA with renewal enabled' : 'AASTHA with renewal disabled');
    } catch (err) {
      setStatus(s => ({ ...s, renewalAastha: !next }));
      toast.error(err.response?.data?.message || 'Failed to update');
    } finally {
      setTogglingAastha(false);
    }
  }

  async function handleToggleLongTermAutoStart(next) {
    setTogglingLongTerm(true);
    setStatus(s => ({ ...s, longTermAutoStart: next }));
    try {
      await gtplSaathiAPI.setLongTermAutoStart(next);
      toast.success(next ? 'Automatic 6/12-month box swap enabled' : 'Automatic 6/12-month box swap disabled');
    } catch (err) {
      setStatus(s => ({ ...s, longTermAutoStart: !next }));
      toast.error(err.response?.data?.message || 'Failed to update');
    } finally {
      setTogglingLongTerm(false);
    }
  }

  async function handleToggleDisconnectAutomation(next) {
    setTogglingDisconnect(true);
    setStatus(s => ({ ...s, disconnectTicketAutomation: next }));
    try {
      await gtplSaathiAPI.setDisconnectAutomation(next);
      toast.success(next ? 'Disconnect ticket automation enabled' : 'Disconnect ticket automation disabled');
    } catch (err) {
      setStatus(s => ({ ...s, disconnectTicketAutomation: !next }));
      toast.error(err.response?.data?.message || 'Failed to update');
    } finally {
      setTogglingDisconnect(false);
    }
  }

  const broken = !loadingStatus && (!status?.configured || status?.lastTestResult === 'failed');
  const history = status?.sessionHistory?.slice(0, 10) || [];

  return (
    <Screen narrow>
      {/* 1. Is the GTPL login working? Everything else on this screen is a fix or a preference. */}
      {loadingStatus && !status ? <Loading /> : (
        <Notice tone={broken ? 'bad' : 'ok'} icon={broken ? XCircle : CheckCircle}
          title={!status?.configured ? 'No session saved yet' : broken ? 'Not working — session expired' : 'GTPL login is working'}
          actions={<>
            {status?.configured && (
              <button type="button" className="btn btn-sm btn-primary" onClick={handleCheckNow} disabled={checking}>
                <RefreshCw size={14} style={checking ? { animation: 'spin 1s linear infinite' } : undefined} />
                {checking ? 'Checking…' : 'Check now'}
              </button>
            )}
          </>}>
          <div style={{ color: 'var(--text-muted)', fontSize: '.82rem', marginTop: 2 }}>
            {status?.configured && <>Session saved{status.loginUsername ? ` for ${status.loginUsername}` : ''}</>}
            {status?.lastLoginAt && <> · last pasted {fmtDateTime(status.lastLoginAt)}</>}
            {status?.lastTestedAt && <> · checked {fmtRelative(status.lastTestedAt)}</>}
          </div>
          {broken && <div style={{ marginTop: 4 }}>Log in again below. Actions wait in the Retry Queue until then.</div>}
        </Notice>
      )}

      {/* 2. Ways to fix the login. Open when it is broken, folded away when it is fine. */}
      <GtplSaathiAutoLogin status={status} onChanged={loadStatus} />

      <Panel icon={ClipboardPaste} tone="info" collapsible open={broken} title="Paste a fresh session"
        sub="Manual way: copy the Cookie from Chrome after logging in">
        <ol style={{ fontSize: '.86rem', color: 'var(--text-muted)', paddingLeft: 20, margin: '0 0 14px', lineHeight: 1.8 }}>
          <li>Log into <strong>gtplsaathi.com</strong> in Chrome, like normal.</li>
          <li>Open DevTools (right-click the page → Inspect), go to the <strong>Network</strong> tab.</li>
          <li>Click any request to gtplsaathi.com in the list (reload the page if the list is empty).</li>
          <li>In <strong>Request Headers</strong>, find the line starting with <strong>Cookie:</strong> and copy everything after the colon.</li>
          <li>Paste it below and save.</li>
        </ol>
        <textarea className="form-control font-mono" rows={4} placeholder="ASP.NET_SessionId=...; .SOMEAUTHCOOKIE=...; ..."
          value={cookie} onChange={e => setCookie(e.target.value)} style={{ fontSize: '.8rem' }} />
        <div className="gx-hint">
          This session dies eventually (GTPL doesn't publish how long it lasts) — when
          search/renew/suspend calls start failing, repeat these steps.
        </div>
        <div className="gx-sticky">
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || !cookie.trim()}>
            <KeyRound size={15} />{saving ? 'Saving…' : 'Save Session'}
          </button>
        </div>
      </Panel>

      {/* 3. Automatic jobs and display preferences. */}
      <Panel icon={SlidersHorizontal} tone="adv" title="Automatic jobs and display" sub="What the CRM does on its own">
        <SwitchRow title="Show GTPL in the sidebar" checked={status?.showInNav !== false}
          onChange={handleToggleNav} disabled={loadingStatus || togglingNav} onText="Shown" offText="Hidden">
          Shows or hides the "GTPL" section in the sidebar. Only visible to you either way — switch it off any time
          you want it out of the way.
        </SwitchRow>
        <SwitchRow title="Disconnect ticket automation" checked={!!status?.disconnectTicketAutomation}
          onChange={handleToggleDisconnectAutomation} disabled={loadingStatus || togglingDisconnect}>
          When enabled, closing a regular-billing CATV customer's connection (Close Connection, on the customer's own
          page) offers to raise a GTPL Saathi "PACKAGE DISCONNECT REQUEST" eSupport ticket for that account — either
          right away or scheduled for a later date (defaults to the 1st of next month). Reactivating the connection
          before a scheduled ticket goes out cancels it automatically.
        </SwitchRow>
        <SwitchRow title="AASTHA with 1-month renewal" checked={status?.renewalAastha !== false}
          onChange={handleToggleRenewalAastha} disabled={loadingStatus || togglingAastha}>
          When enabled, a 1-month DTD CableTV renewal that goes through on GTPL Saathi (base pack extended or newly
          ordered) also orders AASTHA for the same month, so both have 1 month. If the account already has AASTHA
          nothing is ordered. AASTHA is added only after the base pack succeeds; if it fails you get an alert and the
          renewal itself is unaffected. Orders run one at a time. Only 1-month renewals for now.
        </SwitchRow>
        <SwitchRow title="Automatic 6/12-month box swap" checked={!!status?.longTermAutoStart}
          onChange={handleToggleLongTermAutoStart} disabled={loadingStatus || togglingLongTerm}>
          When enabled, a collected 6- or 12-month CableTV recharge (online, staff or agent) starts the box swap and
          GTPL order on its own — the same steps as the button on the customer's GTPL Saathi tab. A regular customer's
          swap runs the next day after 9:30 AM and they become DTD when the months they paid for end; a DTD customer's
          runs straight away (after 9:30 AM tomorrow if paid after 8 PM). Payers see a pop-up first. A short wallet
          waits for a top-up and alerts you; any failed step stops the run and alerts you. Off by default.
        </SwitchRow>
      </Panel>

      {history.length > 0 && (
        <Panel icon={History} tone="mute" collapsible open={false} title="Session history" count={history.length}
          sub="How long each pasted session lasted before it expired">
          <p className="gx-hint" style={{ margin: '0 0 10px' }}>
            Newest first. Useful for telling whether the keep-alive ping (every 3 min) is actually extending session
            life, or GTPL just expires it on a fixed clock regardless.
          </p>
          <div className="gx-list">
            {history.map((h, i) => (
              <Item key={i} title={`Survived ${fmtDuration(h.survivedMinutes) || '—'}`}
                meta={<><span>Pasted <b>{fmtDateTime(h.pastedAt)}</b></span><span>Expired <b>{fmtDateTime(h.expiredAt)}</b></span></>} />
            ))}
          </div>
        </Panel>
      )}
    </Screen>
  );
}
